import { FC, useCallback, useMemo, useState } from 'react';
import {
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  DragOutlined,
  EditOutlined,
  ShareAltOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Image, MenuProps, Space, Typography } from 'antd';
import { MenuInfo } from 'rc-menu/lib/interface';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useDashboardProcsContext, StatusField } from '@/entities/proc';
import { useNotificationContext } from '@/features/notifications';
import { ProcShareModal } from '@/features/proc-management';
import {
  ProcDetailsResponse,
  WorkspaceResponse,
  useGetWorkspaces,
  useProcActions,
  useProcDelete,
  useProcDuplicate,
  useProcUnpublish,
} from '@/shared/api';
import { useRouterPrivateContext, useRouterContext } from '@/shared/router';
import { useThemeContext, themeConfig } from '@/shared/theme';
import {
  Col,
  ConfirmModal,
  ContextMenu,
  IconTag,
  ImagePlaceholder,
  Row,
} from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';

type Props = {
  proc: ProcDetailsResponse;
};

enum MenuOptions {
  Share = 'share',
  Duplicate = 'duplicate',
  Unpublish = 'unpublish',
  Move = 'move',
  GoToInstances = 'goToInstances',
  Edit = 'edit',
  Delete = 'delete',
}

const { Title } = Typography;

export const MyProcContent: FC<Props> = ({
  proc: { id, baseProcId, groupId, title, state, imageLink, uploadedImageLink },
}) => {
  const isPublishedVersion = state === 'published';
  const isPublished = !!baseProcId || isPublishedVersion;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { refetchProcs } = useDashboardProcsContext();
  const { activeOrganization } = useRouterContext();
  const { selectedWorkspace } = useRouterPrivateContext();
  const { showNotification } = useNotificationContext();

  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [deleteProcModalOpen, setDeleteProcModalOpen] = useState(false);

  const { data: workspacesData, refetch: refetchWorkspaces } = useGetWorkspaces<
    WorkspaceResponse[]
  >({}, { select: ({ data }) => data });
  const { mutate: duplicateProc } = useProcDuplicate();
  const { mutate: deleteProc, isLoading: deleteProcLoading } = useProcDelete();
  const { mutate: moveProc } = useProcActions();
  const { mutate: unpublishProc } = useProcUnpublish();

  const handleProcEditOrDuplicate = useCallback(
    async (editCurrentProc = false) => {
      try {
        duplicateProc(
          {
            pathParams: { id },
            queryParams: { linkToBase: editCurrentProc },
          },
          {
            onSuccess: (data) => {
              refetchProcs();
              navigate(`/dashboard/author/proc/${data.id}`);
            },
            onError: (error) => {
              captureError(error, {
                module: 'my_proc_content_on_error_duplicate_proc',
                action: 'duplicate_proc',
              });

              showNotification({
                type: 'error',
                message: 'Duplicate error',
                description: error.message,
              });
            },
          },
        );
      } catch (error) {
        captureError(error, {
          module: 'my_proc_content',
          action: 'duplicate_proc',
        });
      }
    },
    [duplicateProc, id, navigate, refetchProcs, showNotification],
  );

  const handleProcMove = useCallback(
    async (workspaceId: string) => {
      try {
        moveProc(
          {
            pathParams: { id },
            body: {
              workspaceId,
            },
          },
          {
            onSuccess: () => {
              refetchProcs();
              refetchWorkspaces();
              showNotification({
                type: 'success',
                message: 'Proc moved',
                description: 'Proc moved to selected workspace.',
              });
            },
            onError: (error) => {
              captureError(error, {
                module: 'my_proc_content_on_error_move_proc',
                action: 'move_proc',
              });

              showNotification({
                type: 'error',
                message: 'Proc move error',
                description: error.message,
              });
            },
          },
        );
      } catch (error) {
        captureError(error, {
          module: 'my_proc_content_on_error_move_proc',
          action: 'move_proc',
        });
      }
    },
    [id, moveProc, refetchProcs, refetchWorkspaces, showNotification],
  );

  const handleProcDelete = useCallback(async () => {
    try {
      deleteProc(
        { pathParams: { id: id! } },
        {
          onSuccess() {
            refetchProcs();
            showNotification({
              type: 'success',
              message: 'Proc deleted',
              description: 'Proc successfully deleted',
            });
            if (selectedWorkspace) {
              queryClient.invalidateQueries([
                'workspaces',
                'organizations',
                activeOrganization?.id,
                'my',
              ]);
            }
          },
          onError(error) {
            captureError(error, {
              module: 'my_proc_content_on_error_delete_proc',
              action: 'delete_proc',
            });

            showNotification({
              type: 'error',
              message: 'Proc delete error',
              description: error.message,
            });

            captureError(error, {
              module: 'my_proc_content_on_error_delete_proc',
              action: 'delete_proc',
            });
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'my_proc_content_on_error_delete_proc',
        action: 'delete_proc',
      });
    }
  }, [
    activeOrganization?.id,
    deleteProc,
    id,
    queryClient,
    refetchProcs,
    selectedWorkspace,
    showNotification,
  ]);

  const handleProcUnpublish = useCallback(async () => {
    unpublishProc(
      {
        pathParams: { id },
      },
      {
        onSuccess: () => {
          refetchProcs();
          showNotification({
            type: 'success',
            message: 'Proc unpublished',
            description: 'Proc changed state to draft.',
          });
        },
        onError: (error: any) => {
          if (error.stack?.error === 'InstancesExist') {
            showNotification({
              type: 'error',
              message: 'Proc unpublishing failed',
              description:
                "Proc can't be unpublished because it has instances. Please delete all instances first.",
            });
            captureError(error, {
              module: 'my_proc_content_on_error_unpublish_proc',
              action: 'unpublish_proc',
            });
          }
        },
      },
    );
  }, [id, refetchProcs, showNotification, unpublishProc]);

  const onContextItemClick: MenuProps['onClick'] = useCallback(
    ({ key, domEvent }: MenuInfo) => {
      domEvent.preventDefault();
      switch (key) {
        case MenuOptions.Share:
          return setShareModalVisible(true);
        case MenuOptions.Duplicate:
          return handleProcEditOrDuplicate();
        case MenuOptions.Edit:
          return handleProcEditOrDuplicate(true);
        case MenuOptions.Delete:
          return setDeleteProcModalOpen(true);
        case MenuOptions.Unpublish:
          return handleProcUnpublish();
        default:
          return null;
      }
    },
    [handleProcEditOrDuplicate, handleProcUnpublish],
  );

  const contextMenuOptions: MenuProps['items'] = useMemo(
    () =>
      [
        (isPublished || baseProcId) && {
          label: (
            <Space>
              <UnorderedListOutlined />
              Go to instances
            </Space>
          ),
          key: MenuOptions.GoToInstances,
          onClick: () => navigate(`/dashboard/author/proc/${id}/instances`),
        },
        isPublished && {
          label: (
            <Space>
              <UndoOutlined />
              Unpublish proc
            </Space>
          ),
          key: MenuOptions.Unpublish,
        },
        isPublished && {
          label: (
            <Space>
              <ShareAltOutlined />
              Share proc
            </Space>
          ),
          key: MenuOptions.Share,
        },
        {
          label: (
            <Space>
              <CopyOutlined />
              Duplicate
            </Space>
          ),
          key: MenuOptions.Duplicate,
        },
        Number(workspacesData?.length) > 1 && {
          label: (
            <Space>
              <DragOutlined />
              Move to
            </Space>
          ),
          key: MenuOptions.Move,
          children: workspacesData
            ?.filter((ws: WorkspaceResponse) => ws.id !== selectedWorkspace?.id)
            .map((item: WorkspaceResponse) => ({
              label: item.title,
              key: item.id,
              onClick: () => handleProcMove(item.id),
            })),
        },
        isPublishedVersion && {
          label: (
            <Space>
              <EditOutlined />
              Edit
            </Space>
          ),
          key: MenuOptions.Edit,
        },
        { type: 'divider' },
        {
          label: (
            <Space>
              <DeleteOutlined />
              Delete
            </Space>
          ),
          key: MenuOptions.Delete,
          style: {
            color: themeConfig[theme].colors.error,
          },
        },
      ] as MenuProps['items'],
    [
      isPublished,
      baseProcId,
      workspacesData,
      theme,
      navigate,
      id,
      selectedWorkspace?.id,
      handleProcMove,
    ],
  );

  return (
    <>
      <Wrapper
        data-testid={`proc-card-${title.replaceAll(/\s/g, '-').toLowerCase()}`}
        $backgroundColor={themeConfig[theme].colors.contentBg}
        $borderColor={themeConfig[theme].colors.border}
        $borderHoverColor={themeConfig[theme].colors.borderHover}
        className="proc-card"
        onClick={() => navigate(`/dashboard/author/proc/${id}`)}
      >
        <Header>
          <Actions>
            <StatusField status={state} />
            {baseProcId && (
              <IconTag title="The proc has published version" color="green">
                <CheckOutlined />
              </IconTag>
            )}
            <div style={{ marginLeft: 'auto' }}>
              <ContextMenu
                onClick={onContextItemClick}
                options={contextMenuOptions}
              />
            </div>
          </Actions>
          <Title
            level={5}
            ellipsis={{ rows: 2 }}
            style={{
              lineHeight: 1.3,
              paddingInline: 8,
              height: 42,
              overflow: 'hidden',
              marginTop: 8,
            }}
          >
            {title}
          </Title>
        </Header>
        <Content>
          {imageLink || uploadedImageLink ? (
            <Image
              preview={false}
              src={imageLink || uploadedImageLink}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3PTWBSGcbGzM6GCKqlIBRV0dHRJFarQ0eUT8LH4BnRU0NHR0UEFVdIlFRV7TzRksomPY8uykTk/zewQfKw/9znv4yvJynLv4uLiV2dBoDiBf4qP3/ARuCRABEFAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghgg0Aj8i0JO4OzsrPv69Wv+hi2qPHr0qNvf39+iI97soRIh4f3z58/u7du3SXX7Xt7Z2enevHmzfQe+oSN2apSAPj09TSrb+XKI/f379+08+A0cNRE2ANkupk+ACNPvkSPcAAEibACyXUyfABGm3yNHuAECRNgAZLuYPgEirKlHu7u7XdyytGwHAd8jjNyng4OD7vnz51dbPT8/7z58+NB9+/bt6jU/TI+AGWHEnrx48eJ/EsSmHzx40L18+fLyzxF3ZVMjEyDCiEDjMYZZS5wiPXnyZFbJaxMhQIQRGzHvWR7XCyOCXsOmiDAi1HmPMMQjDpbpEiDCiL358eNHurW/5SnWdIBbXiDCiA38/Pnzrce2YyZ4//59F3ePLNMl4PbpiL2J0L979+7yDtHDhw8vtzzvdGnEXdvUigSIsCLAWavHp/+qM0BcXMd/q25n1vF57TYBp0a3mUzilePj4+7k5KSLb6gt6ydAhPUzXnoPR0dHl79WGTNCfBnn1uvSCJdegQhLI1vvCk+fPu2ePXt2tZOYEV6/fn31dz+shwAR1sP1cqvLntbEN9MxA9xcYjsxS1jWR4AIa2Ibzx0tc44fYX/16lV6NDFLXH+YL32jwiACRBiEbf5KcXoTIsQSpzXx4N28Ja4BQoK7rgXiydbHjx/P25TaQAJEGAguWy0+2Q8PD6/Ki4R8EVl+bzBOnZY95fq9rj9zAkTI2SxdidBHqG9+skdw43borCXO/ZcJdraPWdv22uIEiLA4q7nvvCug8WTqzQveOH26fodo7g6uFe/a17W3+nFBAkRYENRdb1vkkz1CH9cPsVy/jrhr27PqMYvENYNlHAIesRiBYwRy0V+8iXP8+/fvX11Mr7L7ECueb/r48eMqm7FuI2BGWDEG8cm+7G3NEOfmdcTQw4h9/55lhm7DekRYKQPZF2ArbXTAyu4kDYB2YxUzwg0gi/41ztHnfQG26HbGel/crVrm7tNY+/1btkOEAZ2M05r4FB7r9GbAIdxaZYrHdOsgJ/wCEQY0J74TmOKnbxxT9n3FgGGWWsVdowHtjt9Nnvf7yQM2aZU/TIAIAxrw6dOnAWtZZcoEnBpNuTuObWMEiLAx1HY0ZQJEmHJ3HNvGCBBhY6jtaMoEiJB0Z29vL6ls58vxPcO8/zfrdo5qvKO+d3Fx8Wu8zf1dW4p/cPzLly/dtv9Ts/EbcvGAHhHyfBIhZ6NSiIBTo0LNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiEC/wGgKKC4YMA4TAAAAABJRU5ErkJggg=="
              style={{ objectFit: 'cover', height: 140, borderRadius: 8 }}
            />
          ) : (
            <ImagePlaceholder style={{ height: 140 }} />
          )}
        </Content>
      </Wrapper>
      {shareModalVisible && (
        <ProcShareModal
          open={shareModalVisible}
          procGroupId={groupId}
          onCancel={() => setShareModalVisible(false)}
        />
      )}
      <ConfirmModal
        dataTestid="delete-proc-modal"
        open={deleteProcModalOpen}
        title="Delete proc"
        text={
          <span>
            Are you sure you want to delete this proc: <strong>{title}</strong>?
          </span>
        }
        confirmBtnText="Delete"
        loading={deleteProcLoading}
        onConfirm={handleProcDelete}
        onCancel={() => setDeleteProcModalOpen(false)}
      />
    </>
  );
};

const Wrapper = styled.div<{
  $backgroundColor: string;
  $borderColor: string;
  $borderHoverColor: string;
}>`
  border-width: 1px;
  border-style: solid;
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.16) 0 1px 4px;
  cursor: pointer;
  transition: all 0.25s ease-in-out;
  background-color: ${({ $backgroundColor }) => $backgroundColor} !important;
  border-color: ${({ $borderColor }) => $borderColor} !important;
  &:hover {
    border-color: ${({ $borderHoverColor }) => $borderHoverColor} !important;
  }
`;

const Header = styled.div`
  padding: 8px;
  padding-bottom: 0;
  color: rgba(0, 0, 0, 0.88);
  width: 100%;
  font-size: 12px;
`;

const Actions = styled(Row)`
  justify-content: space-between;
  margin-bottom: 5px;
  padding-left: 8px;
`;

const Content = styled(Col)`
  width: 100%;
  gap: 15px;
  padding: 8px;
  & .ant-image {
    width: 100%;
  }
`;
